import { IGenericObject } from 'models';
import * as ReactGA from 'react-ga';

let _isGoogleInitialized = false;

interface IGooglePageView {
  type: string;
  payload: string;
}

interface IGoogleEvent {
  type: string;
  payload: { category: string; action: string; label: string };
}

interface IGoogleModalView {
  type: string;
  payload: string;
}

interface IGoogleSetOptions {
  type: string;
  payload: IGenericObject;
}

let googleQueue: Array<IGoogleEvent | IGooglePageView | IGoogleModalView | IGoogleSetOptions> = [];

const GA_LOCAL_STORAGE_KEY = 'ga:clientId';

export enum GAEventCategory {
  SITE = 'Site',
}

export enum GAEventAction {
  CLICK = 'Click',
}

export enum GAEventLabel {
  CAROUSEL_SLIDE = 'Carousel Slide',
  SPEAKER_BIO = 'Speaker Bio',
  QUICK_LINK = 'Quick Link',
}

export const initializeGoogleAnalytics = (gaId: string) => {
  const gaOptions: ReactGA.GaOptions = {
    storage: 'none',
    clientId: localStorage.getItem(GA_LOCAL_STORAGE_KEY) || undefined,
  };
  const initOptions: ReactGA.InitializeOptions = {
    gaOptions: gaOptions,
  };

  ReactGA.initialize(gaId, initOptions);

  _isGoogleInitialized = true;

  if (googleQueue.length > 0) {
    googleQueue.forEach((call: IGooglePageView | IGoogleEvent | IGoogleSetOptions) => {
      switch (call.type) {
        case 'page':
          trackGooglePage((call as IGooglePageView).payload);
          break;
        case 'event':
          const { category, action, label } = (call as IGoogleEvent).payload;
          trackGoogleEvent(category, action, label);
          break;
        case 'modal':
          trackGoogleModal((call as IGoogleModalView).payload);
          break;
        case 'set':
          ReactGA.set((call as IGoogleSetOptions).payload);
      }
    });

    googleQueue = [];
  }
};

export const initializeVimeoAnalytics = () => {
  const scriptId = 'vimeo-ga-script';

  if (document.getElementById(scriptId)) return;

  const { head } = document;
  const script = document.createElement('script');
  script.id = scriptId;
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://extend.vimeocdn.com/ga/103567477.js';
  head.insertBefore(script, head.firstChild);
};

export const isInitialized = () => {
  return _isGoogleInitialized;
};

export const trackGoogleEvent = (category: string, action: string, label: string = '') => {
  if (_isGoogleInitialized) {
    ReactGA.event({ category, action, label });
  } else {
    googleQueue.push({
      payload: { category, action, label },
      type: 'event',
    });
  }
};

export const trackGooglePage = (page: string) => {
  if (_isGoogleInitialized) {
    ReactGA.pageview(page);
  } else {
    googleQueue.push({
      payload: page,
      type: 'page',
    });
  }
};

export const trackGoogleModal = (modal: string) => {
  if (_isGoogleInitialized) {
    ReactGA.modalview(modal);
  } else {
    googleQueue.push({
      payload: modal,
      type: 'modal',
    });
  }
};

/**
 * The following functions are for tracking events in GA in the way we need them to be
 * able to slice the data in the way we want for TSMC. The index values for the custom dimensions
 * and metrics must match exactly what is in GA. If they are changed in GA, they **MUST** be updated here.
 *
 */

/**
 * Sets custom data about a user to track in Google Analytics. Custom Dimensions must be
 * set up in GA dashboard
 * Dimension1 = User Category
 * Dimension2 = User Company
 * Dimension4 = UserId
 * @param userId UserId to track
 * @param company User's Company name
 */
export const setGoogleUser = (userId: string, company: string) => {
  if (_isGoogleInitialized) {
    ReactGA.set({ userId, dimension1: userId.charAt(0), dimension2: company, dimension4: userId });
  } else {
    googleQueue.push({
      payload: { userId, dimension1: userId.charAt(0), dimension2: company, dimension4: userId },
      type: 'set',
    });
  }
};

export const hitTimestamp = () => {
  return new Date().toISOString();
};

export const trackVideoPlay = (title: string) => {
  ReactGA.ga('send', 'event', 'Video', 'Video Play', title, {
    dimension7: title,
    metric1: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVideoComplete = (title: string) => {
  ReactGA.ga('send', 'event', 'Video', 'Video Complete', title, {
    dimension7: title,
    metric6: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVideoProgress = (title: string, progressPercentage: number) => {
  const percentString = (progressPercentage * 100).toString();
  let metricIndex = '';

  switch (percentString) {
    case '2':
      metricIndex = 'metric8';
      break;
    case '25':
      metricIndex = 'metric3';
      break;
    case '50':
      metricIndex = 'metric4';
      break;
    case '75':
      metricIndex = 'metric5';
      break;
  }
  ReactGA.ga('send', 'event', 'Video', `Video Progress ${percentString}%`, title, {
    dimension7: title,
    [metricIndex]: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVideoTimeWatched = (title: string, timeWatched: number) => {
  ReactGA.ga('send', 'event', 'Video', 'Video Time Watched', title, {
    dimension7: title,
    metric7: timeWatched,
    dimension5: hitTimestamp(),
  });
};

export const trackVendorPageView = (vendorName: string, page: string) => {
  ReactGA.ga('send', 'pageview', {
    title: vendorName,
    page,
    dimension6: vendorName,
    dimension5: hitTimestamp(),
  });
};

export const trackResourceClick = (vendorName: string, resourceTitle: string) => {
  ReactGA.ga('send', 'event', 'Vendor', 'Resource Click', resourceTitle, {
    dimension6: vendorName,
    dimension8: resourceTitle,
    metric2: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVendorEmailButtonClick = (vendorName: string) => {
  ReactGA.ga('send', 'event', 'Vendor', 'Email Button Click', {
    dimension6: vendorName,
    metric9: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVendorChatButtonClick = (vendorName: string) => {
  ReactGA.ga('send', 'event', 'Vendor', 'Live Chat Button Click', {
    dimension6: vendorName,
    metric10: 1,
    dimension5: hitTimestamp(),
  });
};

export const trackVendorSocialLinkClick = (vendorName: string, label: string) => {
  ReactGA.ga('send', 'event', 'Vendor', 'Social Link Click', label, {
    dimension6: vendorName,
    dimension5: hitTimestamp(),
  });
};
